/**
 * @description : This file does the ticekting update, cancel, and change for the flight booking - Mainly used for the flight post booking
 * @author: Azamuddin
 * @date: 07/27/2025
 */

//********IMPORTS STARTS*********/
import React, { createContext, useContext, useReducer, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
    Box, Button, Collapse, IconButton, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, MenuItem, FormControl,
    Select, SelectChangeEvent, ListItemText, Checkbox, OutlinedInput, TextField,
    InputAdornment, FormHelperText, Snackbar, Alert, Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    List,
    ListItem
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchBookingDetails, updateFlightBooking } from '../operations/FlightManagement';

//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const actionTypes = ['Ticketing Update','Confirm Booking', 'Modify', 'Cancellation'];
const bookingStatuses = ['Pending', 'Inprogress', 'Confirmed'];
const paymentStatuses = ['Paid', 'Pending', 'Failed', 'Expired'];
const titles = ['Mr', 'Mrs', 'Ms'];
const genders = ['Male', 'Female', 'Other'];
const statuses = ['Pending', 'Failed', 'Confirmed', 'Ticketed'];

//********CONSTANTS ENDS*********/

//********INTERFACES STARTS*********/
interface Segment {
    marketingAirline: string;
    operatingAirline: string;
    origin: string;
    destination: string;
    departureTime: string;
    arrivalTime: string;
    flightNumber: string;
    cabinClass: string;
    fareClass: string; // RBD
    checkedBaggage: string;
    cabinBaggage: string;
    meals: string;
    seat: string;
    aircraft: string;
    duration: string;
    departureTerminal: string;
    arrivalTerminal: string;
    airline_pnr?: string;
    [key: string]: any; // For any other properties that might be in the API response
}

interface Sector {
    departure: string;
    arrival: string;
    journeyType: string; // Onward/Return
    pnr: string;
    officeId: string;
    mondeeProductId?: string;
    mondeeTripId?: string;
    duffelOrderId?: string;
    mystiflyUniqueId?: string;
    segments: Segment[];
    supplier?: string;
    supplierSource?: string;
    [key: string]: any; // For any other properties that might be in the API response
}

// Define the interface for the passenger data structure
interface PassengerDetail {
    passengerId: string;
    title: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    dateOfBirth?: string;
    passengerType: string;
    nationality?: string;
    ticketNumber: string;
    fareAmount: string;
    taxAmount: string;
    markupAmount: string;
    serviceFeeAmount: string;
    totalAmount: string;
    status: string;
    seatNumber?: string;
    mealPreference?: string;
    baggagePreference?: string;
    specialAssistance?: string;
    frequentFlyerNumber?: string;
    frequentFlyerAirline?: string;
    sectorPricing?: {
        transactionId: string;
        sectorId: string;
        route: string;
        fareAmount: string;
        taxAmount: number;
        totalAmount: string;
        ticketNumber: string;
        status: string;
    }[];
    additionalIds?: string[];
}
// Update FlightFormState to use our defined types
interface FlightFormState {
    // Form fields
    actionType: string;
    bookingId: string;
    bookingStatus: string;
    paymentStatus: string;
    amount: string;
    flightType: string;
    cabinClass: string;
    bookingDate: dayjs.Dayjs | null;
    value: string;
    passengerDetails: PassengerDetail[];
    sectorDetails: Sector[];
    // UI state
    open1: boolean;
    open2: boolean;
    open3: boolean;
    open4: boolean;
    dobOpen: boolean;
    bookingDateOpen: boolean;

    // API state
    isLoading: boolean;
    error: string | null;
}

// Define type for the changes object
interface Changes {
    passengers: Record<string, any>;
    sectors: Record<number, any>;
}

interface SectorErrorFields {
    mondeeProductId?: string;
    mondeeTripId?: string;
    mystiflyUniqueId?: string;
    duffelOrderId?: string;
    segments?: Record<number, string>; 
}
interface SectorValidationErrors {
    sectors: Record<number, SectorErrorFields>;
}
//********INTERFACES ENDS*********/

//********REDUCER STARTS*********/
type FlightFormAction =
    | { type: 'SET_FIELD'; field: keyof FlightFormState; value: any }
    | { type: 'TOGGLE_SECTION'; section: 'open1' | 'open2' | 'open3' | 'open4' | 'dobOpen' | 'bookingDateOpen' }
    | { type: 'SET_LOADING'; isLoading: boolean }
    | { type: 'SET_ERROR'; error: string | null }
    | { type: 'RESET_FORM' }
    | { type: 'LOAD_BOOKING_DATA'; data: Partial<FlightFormState> }
    | { type: 'UPDATE_SECTOR_FIELD'; sectorIndex: number; field: string; value: any }
    | { type: 'UPDATE_SEGMENT_FIELD'; sectorIndex: number; segmentIndex: number; field: string; value: any }
    | { type: 'UPDATE_PASSENGER_FIELD'; passengerId: string; field: string; value: any }
    | { type: 'UPDATE_PASSENGER_SECTOR_PRICING'; passengerId: string; sectorPricingIndex: number; field: string; value: any };
// Initial state
const initialState: FlightFormState = {
    actionType: '',
    bookingId: '',
    bookingStatus: '',
    paymentStatus: '',
    amount: '',
    flightType: '',
    cabinClass: '',
    bookingDate: null,
    value: '',
    passengerDetails: [],
    sectorDetails: [],
    open1: false,
    open2: false,
    open3: false,
    open4: false,
    dobOpen: false,
    bookingDateOpen: false,

    isLoading: false,
    error: null
};

// Reducer function
// Update the reducer to handle the new action types
function flightFormReducer(state: FlightFormState, action: FlightFormAction): FlightFormState {
    switch (action.type) {
        case 'SET_FIELD':
            return {
                ...state,
                [action.field]: action.value
            };
        case 'TOGGLE_SECTION':
            return {
                ...state,
                [action.section]: !state[action.section]
            };
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.isLoading
            };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.error
            };
        case 'RESET_FORM':
            return initialState;
        case 'LOAD_BOOKING_DATA':
            return {
                ...state,
                ...action.data
            };
        case 'UPDATE_SECTOR_FIELD': {
            const updatedSectors = [...state.sectorDetails];
            if (updatedSectors[action.sectorIndex]) {
                updatedSectors[action.sectorIndex] = {
                    ...updatedSectors[action.sectorIndex],
                    [action.field]: action.value
                };
            }
            return {
                ...state,
                sectorDetails: updatedSectors
            };
        }
        case 'UPDATE_SEGMENT_FIELD': {
            const updatedSectors = [...state.sectorDetails];
            if (
                updatedSectors[action.sectorIndex] &&
                updatedSectors[action.sectorIndex].segments &&
                updatedSectors[action.sectorIndex].segments[action.segmentIndex]
            ) {
                updatedSectors[action.sectorIndex].segments[action.segmentIndex] = {
                    ...updatedSectors[action.sectorIndex].segments[action.segmentIndex],
                    [action.field]: action.value
                };
            }
            return {
                ...state,
                sectorDetails: updatedSectors
            };
        }
        case 'UPDATE_PASSENGER_FIELD': {
            const updatedPassengers = state.passengerDetails.map(passenger =>
                passenger.passengerId === action.passengerId
                    ? { ...passenger, [action.field]: action.value }
                    : passenger
            );

            return {
                ...state,
                passengerDetails: updatedPassengers
            };
        }

        case 'UPDATE_PASSENGER_SECTOR_PRICING': {
            const updatedPassengers = state.passengerDetails.map(passenger => {
                if (passenger.passengerId !== action.passengerId || !passenger.sectorPricing) {
                    return passenger;
                }

                const updatedSectorPricing = [...passenger.sectorPricing];
                if (updatedSectorPricing[action.sectorPricingIndex]) {
                    updatedSectorPricing[action.sectorPricingIndex] = {
                        ...updatedSectorPricing[action.sectorPricingIndex],
                        [action.field]: action.value
                    };
                }

                return {
                    ...passenger,
                    sectorPricing: updatedSectorPricing
                };
            });

            return {
                ...state,
                passengerDetails: updatedPassengers
            };
        }
        default:
            return state;
    }
}

// Custom hook for checking if component is rendered
const useRendered = () => {
    const rendered = useRef(false);

    useEffect(() => {
        rendered.current = true;
        return () => {
            rendered.current = false;
        };
    }, []);

    return rendered.current;
};

// This maps action types to the fields that should be editable for each action
const actionTypePermissions = {
    'Ticketing Update': ['ticketNumber', 'status', 'ticketingDate'],
    'Cancel': ['cancellationReason', 'cancellationFee', 'refundAmount', 'status'],
    'Modify': ['firstName', 'lastName', 'title', 'dateOfBirth', 'nationality', 'email', 'phone'],
    'Reissue': ['ticketNumber', 'fareAmount', 'taxAmount', 'penaltyAmount', 'status'],
    'Void': ['voidReason', 'status'],
    'Refund': ['refundAmount', 'refundReason', 'status', 'penaltyAmount'],
    // Add other action types with their editable fields
    'default': [] // Default case: nothing is editable
};

//********REDUCER ENDS*********/

//********HOOKS STARTS*********/
function useFieldPermissions(actionType: string | undefined) {
    // Create memoized permissions to avoid unnecessary re-renders
    const permissions = useMemo(() => {
        if (!actionType) return actionTypePermissions.default;
        return actionTypePermissions[actionType as keyof typeof actionTypePermissions] || actionTypePermissions.default;
    }, [actionType]);

    // Function to check if a field is editable
    const isFieldEditable = useCallback(
        (fieldName: string) => {
            return permissions.includes(fieldName);
        },
        [permissions]
    );

    // Return the permissions and helper function
    return { permissions, isFieldEditable };
}
//********HOOKS ENDS*********/
const FlightManagement = () => {
    //********INTIATE STATE STARTS*********/
    const [state, dispatch] = useReducer(flightFormReducer, initialState);
    const { isFieldEditable } = useFieldPermissions(state.actionType);
    const isRendered = useRendered();
    const [actionType, setActionType] = useState('');
    const [bookingStatus, setBookingStatus] = useState('');
    const [errors, setErrors] = useState<SectorValidationErrors>({
        sectors: {},
    });

    const [open1, setOpen1] = useState(false);


    const [bookingDateOpen, setBookingDateOpen] = useState(false);
    const [bookingDate, setBookingDate] = useState<dayjs.Dayjs | null>(null);

    const [showNoDataAlert, setShowNoDataAlert] = useState(false);


    const [value, setValue] = useState('');
    // For tracking changes
    const [originalData, setOriginalData] = useState<{
        passengerDetails: PassengerDetail[];
        sectorDetails: Sector[];
    } | null>(null);
    const [changedFields, setChangedFields] = useState<Changes>({
        passengers: {},
        sectors: {}
    });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [expandedPassengers, setExpandedPassengers] = useState<Record<string, boolean>>({});
    const sectorRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const [expandedSectorAccordion, setExpandedSectorAccordion] = useState(false);
    //********INTIATE STATE ENDS*********/

    //********ACTION HANDLERS STARTS******** */
    const setField = (field: keyof FlightFormState, value: any) => {
        dispatch({ type: 'SET_FIELD', field, value });
    };

    const toggleSection = (section: 'open1' | 'open2' | 'open3' | 'open4' | 'dobOpen' | 'bookingDateOpen') => {
        dispatch({ type: 'TOGGLE_SECTION', section });
    };

    const setLoading = (isLoading: boolean) => {
        dispatch({ type: 'SET_LOADING', isLoading });
    };

    const setError = (error: string | null) => {
        dispatch({ type: 'SET_ERROR', error });
    };

    const resetForm = () => {
        dispatch({ type: 'RESET_FORM' });
    };

    const loadBookingData = (data: Partial<FlightFormState>) => {
        setOriginalData(JSON.parse(JSON.stringify(data)));
        dispatch({ type: 'LOAD_BOOKING_DATA', data });
    };

    // Handle form field changes
    const handleSelectChange = (field: keyof FlightFormState) => (event: SelectChangeEvent) => {
        setField(field, event.target.value);
    };

    const handleInputChange = (field: keyof FlightFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setField(field, event.target.value);
    };

    const validateSectors = () => {
        const newErrors: Record<number, SectorErrorFields> = {};

        state.sectorDetails.forEach((sector: Sector, index: number) => {

            if (state.actionType !== "Confirm Booking") return;
            if (!sector.pnr || sector.pnr === "") return;

            const sectorErrors: SectorErrorFields = {};

            // Supplier 1
            if (sector.supplier == '1') {
                if (!sector.mondeeProductId?.trim()) {
                    sectorErrors.mondeeProductId = "Mondee Product ID is required";
                }
                if (!sector.mondeeTripId?.trim()) {
                    sectorErrors.mondeeTripId = "Mondee Trip ID is required";
                }
            }

            // Supplier 2
            if (sector.supplier == '2') {
                if (!sector.mystiflyUniqueId?.trim()) {
                    sectorErrors.mystiflyUniqueId = "Mystifly Unique ID is required";
                }
            }

            // Supplier 3
            if (sector.supplier == '3') {
                if (!sector.duffelOrderId?.trim()) {
                    sectorErrors.duffelOrderId = "Duffel Order ID is required";
                }
            }

            if (sector.segments && sector.segments.length > 0) {

            const segmentErrors: Record<number, string> = {};

            sector.segments.forEach((segment, sIndex) => {
                if (!segment.airline_pnr?.trim()) {
                    segmentErrors[sIndex] = "Airline PNR is required";
                }
            });

            if (Object.keys(segmentErrors).length > 0) {
                sectorErrors.segments = segmentErrors;
            }
        }
            if (Object.keys(sectorErrors).length > 0) {
                newErrors[index] = sectorErrors;
            }
        });

        setErrors({ sectors: newErrors });
        return newErrors;
    };




    // Function to toggle passenger details expansion
    const togglePassengerDetails = (passengerId: string) => {
        setExpandedPassengers(prev => ({
            ...prev,
            [passengerId]: !prev[passengerId]
        }));
    };


    const handleActionTypeChange = (event: SelectChangeEvent) => {
        setField('paymentStatus',event.target.value);
    };
    const handleBookingStatusChange = (event: SelectChangeEvent) => {
        setField('bookingStatus',event.target.value);
    };
    const handleToggle1 = () => {
        setOpen1(!open1);
    };

    //********ACTION HANDLERS ENDS******** */

    //********FUNCTION STARTS*********/

    // Field update handlers
    const updateSectorField = (sectorIndex: number, field: string, value: any) => {
        dispatch({
            type: 'UPDATE_SECTOR_FIELD',
            sectorIndex,
            field,
            value
        });
    };

    const updateSegmentField = (sectorIndex: number, segmentIndex: number, field: string, value: any) => {
        dispatch({
            type: 'UPDATE_SEGMENT_FIELD',
            sectorIndex,
            segmentIndex,
            field,
            value
        });
    };

    // Add this utility function at the top of your component or in a separate utils file
    const formatDateForDisplay = (dateString: string): string => {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) return dateString;

            // Format as MM/DD/YYYY
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${month}/${day}/${year}`;
        } catch (error) {
            // If there's any error parsing the date, return the original string
            return dateString;
        }
    };

    // Add this utility function to parse the stopDetails string
    const getLayoverTimeFromStopDetails = (stopDetails: string | undefined, destination: string): string => {
        if (!stopDetails || !destination) return '';

        try {
            // Split the stopDetails string into individual stop entries
            const stops = stopDetails.split(',');

            // Find the stop that matches the destination
            const matchingStop = stops.find(stop => stop.startsWith(`${destination}|`));

            if (!matchingStop) return '';

            // Extract the time part after the pipe symbol
            const timePart = matchingStop.split('|')[1];

            if (!timePart) return '';

            // Parse the time format (assuming it's in H:MM format)
            const [hours, minutes] = timePart.split(':');

            // Format as HH:mm
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        } catch (error) {
            return '';
        }
    };

    // Handle retrieving booking details
    const handleRetrieveDetails = async () => {
        if (!state.actionType) {
            setError("Please select an action type");
            return;
        }

        if (!state.bookingId) {
            setError("Please enter a booking ID");
            return;
        }

        setLoading(true);
        setError(null);
        setErrors({ sectors: {} });

        try {
            const bookingData = await fetchBookingDetails(state.actionType, state.bookingId);
            // Check if data is available and valid

            if (!bookingData ||
                ((!bookingData.passengerDetails || bookingData.passengerDetails.length === 0) &&
                    (!bookingData.sectors || bookingData.sectors.length === 0))) {

                // Show no data alert
                setShowNoDataAlert(true);

                // Auto-hide alert and reset form after 3 seconds
                setTimeout(() => {
                    dispatch({ type: 'RESET_FORM' });
                }, 3000);

                return;
            }

            // Extract passenger info (using the first passenger if there are multiple)
            const passenger = bookingData.passengerDetails || [];

            // Extract sector info (using the first sector if there are multiple)
            const sector = bookingData.sectors || [];

            loadBookingData({
                sectorDetails: sector,
                passengerDetails: passenger,
                bookingStatus: bookingData.itineraryStatus,
                paymentStatus: bookingData.payStatus,
                amount: bookingData.amount,
                flightType: bookingData?.flowType.toUpperCase() || '',
                bookingDate: bookingData?.bookingDate ? dayjs(bookingData.bookingDate) : null,
            });

            // Open accordion sections
            toggleSection('open1');
            toggleSection('open2');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
            // Show error alert
            setShowNoDataAlert(true);

            // Auto-hide alert and reset form after 3 seconds
            setTimeout(() => {
                dispatch({ type: 'RESET_FORM' });
            }, 3000);
        } finally {
            setLoading(false);
        }
    };


    // Handle update confirmation
    const handleConfirmUpdate = async () => {
        setLoading(true);

        try {
            const response = await updateFlightBooking(state.actionType, state.bookingId, {
                passengerDetails: state.passengerDetails,
                sectors: state.sectorDetails
            });

            alert("Booking updated successfully!");
            setShowConfirmDialog(false);
            // Optionally reset form or redirect
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    type SectorPricingKey = keyof PassengerDetail['sectorPricing'];
    // Function to find changes between original and current state
    const findChanges = (): Changes => {
        if (!originalData) return { passengers: {}, sectors: {} };

        const changes: Changes = {
            passengers: {},
            sectors: {}
        };

        // Check passenger changes
        state.passengerDetails.forEach(passenger => {
            const originalPassenger = originalData.passengerDetails.find(
                p => p.passengerId === passenger.passengerId
            );

            if (originalPassenger) {
                const passengerChanges: Record<string, any> = {};

                // Compare basic passenger fields
                Object.keys(passenger).forEach(key => {
                    const typedKey = key as keyof PassengerDetail;
                    if (typedKey !== 'sectorPricing' &&
                        JSON.stringify(passenger[typedKey]) !== JSON.stringify(originalPassenger[typedKey])) {
                        passengerChanges[typedKey] = {
                            from: originalPassenger[typedKey],
                            to: passenger[typedKey]
                        };
                    }
                });

                // Compare sector pricing
                if (passenger.sectorPricing && originalPassenger.sectorPricing) {
                    passenger.sectorPricing.forEach((pricing, idx) => {
                        const originalPricing = originalPassenger.sectorPricing?.[idx];
                        if (originalPricing) {
                            Object.keys(pricing).forEach(key => {
                                // Use a safer approach with explicit type checking
                                const pricingKey = key as SectorPricingKey;

                                // Type guard to ensure key exists in both objects
                                if (
                                    pricingKey in pricing &&
                                    pricingKey in originalPricing &&
                                    JSON.stringify(pricing[pricingKey]) !== JSON.stringify(originalPricing[pricingKey])
                                ) {
                                    if (!passengerChanges.sectorPricing) {
                                        passengerChanges.sectorPricing = {};
                                    }
                                    if (!passengerChanges.sectorPricing[idx]) {
                                        passengerChanges.sectorPricing[idx] = {};
                                    }
                                    passengerChanges.sectorPricing[idx][pricingKey] = {
                                        from: originalPricing[pricingKey],
                                        to: pricing[pricingKey]
                                    };
                                }
                            });
                        }
                    });
                }

                if (Object.keys(passengerChanges).length > 0) {
                    changes.passengers[passenger.passengerId] = passengerChanges;
                }
            }
        });

        // Check sector changes
        state.sectorDetails.forEach((sector, idx) => {
            // Make sure we're accessing the correct property in originalData
            const originalSector = originalData.sectorDetails[idx];
            if (originalSector) {
                const sectorChanges: Record<string, any> = {};

                // Compare basic sector fields
                Object.keys(sector).forEach(key => {
                    const sectorKey = key as keyof Sector;
                    if (sectorKey !== 'segments' &&
                        JSON.stringify(sector[sectorKey]) !== JSON.stringify(originalSector[sectorKey])) {
                        sectorChanges[sectorKey] = {
                            from: originalSector[sectorKey],
                            to: sector[sectorKey]
                        };
                    }
                });

                // Compare segments
                if (sector.segments && originalSector.segments) {
                    sector.segments.forEach((segment, segIdx) => {
                        const originalSegment = originalSector.segments?.[segIdx];
                        if (originalSegment) {
                            Object.keys(segment).forEach(key => {
                                if (JSON.stringify(segment[key]) !== JSON.stringify(originalSegment[key])) {
                                    if (!sectorChanges.segments) {
                                        sectorChanges.segments = {};
                                    }
                                    if (!sectorChanges.segments[segIdx]) {
                                        sectorChanges.segments[segIdx] = {};
                                    }
                                    sectorChanges.segments[segIdx][key] = {
                                        from: originalSegment[key],
                                        to: segment[key]
                                    };
                                }
                            });
                        }
                    });
                }

                if (Object.keys(sectorChanges).length > 0) {
                    changes.sectors[idx] = sectorChanges;
                }
            }
        });

        return changes;
    };

    // // Handle form submission
    const handleSubmit = () => {

        const sectorValidationErrors = validateSectors();

        if (Object.keys(sectorValidationErrors).length > 0) {
            // Get the first sector with errors
            const firstErrorSectorIndex = Object.keys(sectorValidationErrors)
                .map(Number)
                .sort((a, b) => a - b)[0];
            
            // Open the sector accordion
            setExpandedSectorAccordion(true);
            
            // Scroll to the error sector after a short delay to allow accordion to open
            setTimeout(() => {
                const errorSectorRef = sectorRefs.current[firstErrorSectorIndex];
                if (errorSectorRef) {
                    errorSectorRef.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                }
            }, 300);
            
            alert("Please fill all required fields highlighted in red.");
            return;
        }

        const changes = findChanges();
        setChangedFields(changes);

        // Check if there are any changes
        if (
            Object.keys(changes.passengers).length === 0 &&
            Object.keys(changes.sectors).length === 0
        ) {
            alert("No changes detected. Nothing to update.");
            return;
        }

        // Show confirmation dialog
        setShowConfirmDialog(true);
    };

    /*********FUNCTION ENDS*********/

    //********RETURN COMPONENTS STARTS*********/
    // Confirmation Dialog
const ConfirmationDialog = () => (
  <Dialog 
    open={showConfirmDialog} 
    onClose={() => setShowConfirmDialog(false)}
    fullWidth
    maxWidth="md"
  >
    <CancelIcon onClick={() => setShowConfirmDialog(false)}  className='closeModal' />
    <DialogTitle>Confirm Changes</DialogTitle>
    
    <DialogContent dividers className='subHeadingTitleWrap'>
      <DialogContentText className='subHeadingTitle'>
        The following changes will be applied
      </DialogContentText>
      <Box className="changesBox">
        {Object.keys(changedFields.passengers).length > 0 && (
          <Box className="feeModalForm">
            <Typography variant="subtitle1" gutterBottom>Passenger Changes</Typography>
            {Object.entries(changedFields.passengers).map(([passengerId, changes]) => {
              const passenger = state.passengerDetails.find(p => p.passengerId === passengerId);
              return (
                <Box key={passengerId} className="listItemWrap">
                  <Typography className='commonName' >
                    {passenger?.firstName} {passenger?.lastName}
                  </Typography>
                  
                  <List>
                    {Object.entries(changes).map(([field, change]: [string, any]) => {
                      // Handle nested fields like sectorPricing
                      if (field === 'sectorPricing') {
                        return Object.entries(change).map(([sectorIndex, pricingChanges]: [string, any]) => (
                          <Box key={`pricing-${sectorIndex}`} className="subListWrap">
                            <Typography className="subListTitle" variant="body2" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              color: 'text.secondary'
                            }}>
                              Sector {parseInt(sectorIndex) + 1} Pricing:12
                            </Typography>
                            
                            {Object.entries(pricingChanges).map(([priceField, priceChange]: [string, any]) => (
                              <ListItem 
                                key={`${sectorIndex}-${priceField}`}
                                sx={{ 
                                  backgroundColor: 'rgba(144, 202, 249, 0.1)',
                                  borderRadius: '4px'
                                }}
                              >
                                <ListItemText
                                className='listItemText'
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography component="span" sx={{ fontWeight: 500 }}>
                                        {priceField}:
                                      </Typography>
                                      <Typography 
                                        component="span" 
                                        sx={{ 
                                          textDecoration: 'line-through',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        {priceChange.from}
                                      </Typography>
                                      <ArrowRightAlt sx={{ mx: 0.5, fontSize: '1rem', color: 'primary.main' }} />
                                      <Typography component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                                        {priceChange.to}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={`Changed ${priceField}`}
                                />
                              </ListItem>
                            ))}
                          </Box>
                        ));
                      }

                      return (
                        <ListItem 
                          key={field}
                          sx={{ 
                            backgroundColor: 'rgba(144, 202, 249, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography component="span" sx={{ fontWeight: 500 }}>
                                  {field}:
                                </Typography>
                                <Typography 
                                  component="span" 
                                  sx={{ 
                                    textDecoration: 'line-through',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {change.from}
                                </Typography>
                                <ArrowRightAlt sx={{ mx: 0.5, fontSize: '1rem', color: 'primary.main' }} />
                                <Typography component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {change.to}
                                </Typography>
                              </Box>
                            }
                            secondary={`Changed ${field}`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}

        {/* For sectors, we need to handle the numeric keys */}
        {Object.keys(changedFields.sectors).length > 0 && (
          <Box className="feeModalForm">
            <Typography variant="subtitle1" gutterBottom>Sector Changes</Typography>
            
            {/* Convert string keys back to numbers when iterating */}
            {Object.entries(changedFields.sectors).map(([sectorIndexStr, changes]) => {
              const sectorIndex = parseInt(sectorIndexStr);
              const sector = state.sectorDetails[sectorIndex];

              return (
                <Box key={sectorIndexStr} className="listItemWrap">
                  <Typography className='commonName'>
                    Sector {sectorIndex + 1}: {sector?.departure} to {sector?.arrival}
                  </Typography>
                  
                  <List dense sx={{ mt: 1 }}>
                    {Object.entries(changes).map(([field, change]: [string, any]) => {
                      // Handle nested segments
                      // Continuing from where we left off
                      if (field === 'segments') {
                        return Object.entries(change).map(([segmentIndex, segmentChanges]: [string, any]) => (
                          <Box key={`segment-${segmentIndex}`} className="subListWrap">
                            <Typography className="subListTitle" variant="body2" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              color: 'text.secondary'
                            }}>
                              Segment {parseInt(segmentIndex) + 1}:
                            </Typography>
                            
                            {Object.entries(segmentChanges).map(([segField, segChange]: [string, any]) => (
                              <ListItem 
                                key={`${segmentIndex}-${segField}`}
                                sx={{ 
                                  backgroundColor: 'rgba(129, 199, 132, 0.1)',
                                  borderRadius: '4px'
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography component="span" sx={{ fontWeight: 500 }}>
                                        {segField}:
                                      </Typography>
                                      <Typography 
                                        component="span" 
                                        sx={{ 
                                          textDecoration: 'line-through',
                                          color: 'text.secondary'
                                        }}
                                      >
                                        {segChange.from}
                                      </Typography>
                                      <ArrowRightAlt sx={{ mx: 0.5, fontSize: '1rem', color: 'secondary.main' }} />
                                      <Typography component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                                        {segChange.to}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={`Changed ${segField}`}
                                  secondaryTypographyProps={{ 
                                    sx: { fontSize: '0.75rem', color: 'text.secondary' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </Box>
                        ));
                      }

                      return (
                        <ListItem 
                          key={field}
                          sx={{ 
                            backgroundColor: 'rgba(129, 199, 132, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography component="span" sx={{ fontWeight: 500 }}>
                                  {field}:
                                </Typography>
                                <Typography 
                                  component="span" 
                                  sx={{ 
                                    textDecoration: 'line-through',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {change.from}
                                </Typography>
                                <ArrowRightAlt sx={{ mx: 0.5, fontSize: '1rem', color: 'secondary.main' }} />
                                <Typography component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {change.to}
                                </Typography>
                              </Box>
                            }
                            secondary={`Changed ${field}`}
                            secondaryTypographyProps={{ 
                              sx: { fontSize: '0.75rem', color: 'text.secondary' } 
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}
        
        {/* Show a message if no changes are found */}
        {Object.keys(changedFields.passengers).length === 0 && 
         Object.keys(changedFields.sectors).length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4 
          }}>
            <InfoIcon sx={{ fontSize: '3rem', color: 'info.light', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No changes have been detected.
            </Typography>
          </Box>
        )}
      </Box>
    </DialogContent>
    
    <DialogActions sx={{ 
      px: 2, 
      py: 2, 
      backgroundColor: 'background.default'
    }}
    className='searchInputFilterBox'>
        
      {/* <Button 
        onClick={() => setShowConfirmDialog(false)} 
        color="secondary"
        sx={{ 
          fontWeight: 600, 
          borderRadius: '4px',
          textTransform: 'none',
          px: 3
        }}
      >
        Go Back
      </Button> */}
      <Button 
        className='searchBtn'
        onClick={handleConfirmUpdate} 
        color="primary" 
        variant="contained"
        disabled={state.isLoading}
        startIcon={state.isLoading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {state.isLoading ? 'UPDATING' : 'UPDATE CHANGES'}
      </Button>
    </DialogActions>
  </Dialog>
);

    //*********RETURN COMPONENTS ENDS */
    return (
        <>
            <Snackbar
                open={showNoDataAlert}
                autoHideDuration={3000}
                onClose={() => setShowNoDataAlert(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowNoDataAlert(false)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    The requested booking data is not available or incomplete.
                </Alert>
            </Snackbar>
            <Box className="headingBtnWrap">
                <Typography variant="h1">Flight Management</Typography>
            </Box>
            <Box className="feeModalForm" style={{ marginTop: "20px" }}>
                <Typography variant="subtitle1" gutterBottom>Booking Retrieval</Typography>

                <Box className="feeInputFieldWrap">
                    <div className="form-control inputFieldWrap">
                        <label>Action Type</label>
                        <FormControl
                            sx={{ minWidth: 250 }}
                            className="commondropdownWrap"
                            error={Boolean(state.error && !state.actionType)} // Turn red when there's an error and field is empty
                        >
                            <Select
                                className="commondropdown"
                                id="action-type-select"
                                value={state.actionType}
                                onChange={handleSelectChange('actionType')}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected === '') {
                                        return <span style={{ color: '#888' }}>Select Action Type</span>;
                                    }
                                    return selected;
                                }}
                            >
                                <MenuItem value="">
                                    Select Action Type
                                </MenuItem>
                                {actionTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                            {state.error && !state.actionType && (
                                <div className="error-message" style={{
                                    color: '#d32f2f',
                                    fontSize: '0.75rem',
                                    marginTop: '3px'
                                }}>Please select an action type</div>
                            )}
                        </FormControl>
                    </div>
                    <div className="form-control inputFieldWrap">
                        <label>Booking ID</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                className={`inputField ${state.error && !state.bookingId ? 'error-input' : ''}`}
                                value={state.bookingId}
                                onChange={(e) => handleInputChange('bookingId')(e as any)}
                                placeholder="Enter Booking ID"
                                style={{
                                    borderColor: state.error && !state.bookingId ? '#d32f2f' : undefined
                                }}
                            />
                            {state.error && !state.bookingId && (
                                <div className="error-message" style={{
                                    color: '#d32f2f',
                                    fontSize: '0.75rem',
                                    marginTop: '3px'
                                }}>
                                    Please enter a booking ID
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='searchInputFilterBox fetchPNR'>
                        <Button
                            variant="contained"
                            className="searchBtn"
                            color="primary"
                            type="button"
                            onClick={handleRetrieveDetails} // Add onClick handler to call your function
                            disabled={state.isLoading}
                        >
                            {state.isLoading ? 'Loading...' : 'Retrieve Details'}
                        </Button>
                    </div>
                </Box>
            </Box>

            <Accordion className='flightMgmt'>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                >
                    <Typography component="span">Booking Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className="feeModalForm">
                        <Box className="segmentCard">
                            <Box className="feeInputFieldWrap">
                                <div className="form-control inputFieldWrap">
                                    <label>Flight Type</label>
                                    <input type="text" className="inputField" value={state.flightType} onChange={(e) => handleInputChange('flightType')(e as any)}/>
                                </div>
                                <div className="form-control inputFieldWrap">
                                    <label>Cabin Class</label>
                                    <input type="text" className="inputField" value={state.cabinClass} onChange={(e) => handleInputChange('cabinClass')(e as any)}/>
                                </div>
                            </Box>
                            <Box className="showAdditionalWrap">
                                <Typography onClick={handleToggle1} component="span" className='showAdditional'>
                                    {open1 ? '▼ Hide Additional Details' : '▶ Show Additional Details'}
                                </Typography>
                                <Collapse in={open1}>
                                    <Box className="feeInputFieldWrap">
                                        <div className="form-control inputFieldWrap">
                                            <label>Booking Status</label>
                                            <FormControl sx={{ minWidth: 315 }} className="commondropdownWrap">
                                                <Select
                                                    //className="commondropdown"
                                                    className={state.actionType == 'Confirm Booking' || state.actionType == 'Cancellation' ? "commondropdown notThis" : "commondropdown"}
                                                    id="action-type-select"
                                                    value={state.bookingStatus}
                                                    onChange={handleBookingStatusChange}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (selected === '') {
                                                            return <span style={{ color: '#888' }}>Select Booking Status</span>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        Select Booking Status
                                                    </MenuItem>
                                                    {bookingStatuses.map((status) => (
                                                        <MenuItem key={status} value={status}>
                                                            {status}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <label>Booking Date</label>
                                                <div onClick={() => setBookingDateOpen(true)}> 
                                                    <DatePicker
                                                        open={bookingDateOpen}
                                                        onOpen={() => setBookingDateOpen(true)}
                                                        onClose={() => setBookingDateOpen(false)}
                                                        value={state.bookingDate}
                                                        onChange={(newValue) => handleInputChange('bookingDate')(newValue as any)}
                                                        slotProps={{ 
                                                            textField: { 
                                                            fullWidth: true, 
                                                            InputProps: { readOnly: true } 
                                                            } 
                                                        }}
                                                        disabled
                                                        className='datePickerWrap'
                                                    />
                                                </div>
                                            </LocalizationProvider>
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Total Amount</label>
                                            <input type="text" className="inputField" value={state.amount} onChange={(e) => handleInputChange('amount')(e as any)}/>
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Payment Status</label>
                                            <FormControl sx={{ minWidth: 315 }} className="commondropdownWrap">
                                                <Select
                                                    className="commondropdown"
                                                    id="action-type-select"
                                                    value={state.paymentStatus}
                                                    onChange={handleActionTypeChange}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (selected === '') {
                                                            return <span style={{ color: '#888' }}>Select Payment Status</span>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        Select Payment Status
                                                    </MenuItem>
                                                    {paymentStatuses.map((status) => (
                                                        <MenuItem key={status} value={status}>
                                                            {status}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Booking Agent</label>
                                            <input type="text" className="inputField" />
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Last Modified</label>
                                            <input type="text" className="inputField" />
                                        </div>
                                    </Box>
                                </Collapse>
                            </Box>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Accordion className='flightMgmt' expanded={expandedSectorAccordion} onChange={(e, isExpanded) => setExpandedSectorAccordion(isExpanded)}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                >
                    <Typography component="span">Sector Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className="feeModalForm">
                        {/* Map through sectorDetails array to render each sector */}
                        {state.sectorDetails.map((sector, sectorIndex) => (
                            <Box 
                                key={sectorIndex} 
                                className="segmentCard"
                                ref={(el: HTMLDivElement | null) => { sectorRefs.current[sectorIndex] = el; }}
                            >
                                <div className="segmentHeader">
                                    <div className="flightRoute">
                                        {sector.departure || 'Origin'} → {sector.arrival || 'Destination'} ({sector.supplier_name.toUpperCase()})
                                    </div>
                                    <div className="statusBadge">{sector.journeyType || 'Unknown'}</div>
                                </div>

                                {/* Sector level fields */}
                                <Box className="feeInputFieldWrap">
                                    <div className="form-control inputFieldWrap">
                                        <label>Marketing Airline</label>
                                        <input
                                            type="text"
                                            className="inputField"
                                            value={sector.segments?.[0]?.marketingAirline || ''}
                                            onChange={(e) => updateSegmentField(sectorIndex, 0, 'marketingAirline', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-control inputFieldWrap">
                                        <label>Operating Airline</label>
                                        <input
                                            type="text"
                                            className="inputField"
                                            value={sector.segments?.[0]?.operatingAirline || ''}
                                            onChange={(e) => updateSegmentField(sectorIndex, 0, 'operatingAirline', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-control inputFieldWrap">
                                        <label>Departure</label>
                                        <input
                                            type="text"
                                            className="inputField"
                                            value={sector.departure || ''}
                                            onChange={(e) => updateSectorField(sectorIndex, 'departure', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-control inputFieldWrap">
                                        <label>Arrival</label>
                                        <input
                                            type="text"
                                            className="inputField"
                                            value={sector.arrival || ''}
                                            onChange={(e) => updateSectorField(sectorIndex, 'arrival', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-control inputFieldWrap">
                                        <label>GDS PNR</label>
                                        <input
                                            type="text"
                                            className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                            value={sector.pnr || ''}
                                            onChange={(e) => updateSectorField(sectorIndex, 'pnr', e.target.value)}
                                        />
                                    </div>
                                    {sector.officeId !== '' && (
                                        <div className="form-control inputFieldWrap">
                                            <label>Office ID</label>
                                            <input
                                                type="text"
                                                className="inputField"
                                                value={sector.officeId || ''}
                                                onChange={(e) =>
                                                    updateSectorField(sectorIndex, 'officeId', e.target.value)
                                                }
                                            />
                                        </div>
                                    )}

                                    {sector.officeId === '' && sector.supplier == '1' && (
                                        <>
                                            <div className="form-control inputFieldWrap">
                                                <label>Mondee Product ID</label>
                                                <input
                                                    type="text"
                                                    className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                                    value={sector.mondeeProductId || ''}
                                                    onChange={(e) =>
                                                        updateSectorField(sectorIndex, 'mondeeProductId', e.target.value)
                                                    }
                                                />

                                                {errors.sectors[sectorIndex]?.mondeeProductId && (
                                                    <small className="errorMessage">{errors.sectors[sectorIndex].mondeeProductId}</small>
                                                )}
                                            </div>

                                            <div className="form-control inputFieldWrap">
                                                <label>Mondee Trip ID</label>
                                                <input
                                                    type="text"
                                                    className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                                    value={sector.mondeeTripId || ''}
                                                    onChange={(e) =>
                                                        updateSectorField(sectorIndex, 'mondeeTripId', e.target.value)
                                                    }
                                                />

                                                {errors.sectors[sectorIndex]?.mondeeTripId && (
                                                    <small className="errorMessage">{errors.sectors[sectorIndex].mondeeTripId}</small>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {sector.officeId === '' && sector.supplier == '2' && (
                                        <div className="form-control inputFieldWrap">
                                            <label>Mystifly Unique ID</label>
                                            <input
                                                type="text"
                                                className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                                value={sector.mystiflyUniqueId || ''}
                                                onChange={(e) =>
                                                    updateSectorField(sectorIndex, 'mystiflyUniqueId', e.target.value)
                                                }
                                            />

                                            {errors.sectors[sectorIndex]?.mystiflyUniqueId && (
                                                <small className="errorMessage">{errors.sectors[sectorIndex].mystiflyUniqueId}</small>
                                            )}
                                        </div>
                                    )}

                                    {sector.officeId === '' && sector.supplier == '3' && (
                                        <div className="form-control inputFieldWrap">
                                            <label>Duffel Order ID</label>
                                            <input
                                                type="text"
                                                className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                                value={sector.duffelOrderId || ''}
                                                onChange={(e) =>
                                                    updateSectorField(sectorIndex, 'duffelOrderId', e.target.value)
                                                }
                                            />

                                            {errors.sectors[sectorIndex]?.duffelOrderId && (
                                                <small className="errorMessage">{errors.sectors[sectorIndex].duffelOrderId}</small>
                                            )}
                                        </div>
                                    )}

                                    <div className="form-control inputFieldWrap">
                                        <label>Sector Type</label>
                                        <input
                                            type="text"
                                            className="inputField"
                                            value={sector.journeyType || ''}
                                            onChange={(e) => updateSectorField(sectorIndex, 'journeyType', e.target.value)}
                                        />
                                    </div>
                                </Box>

                                {/* Segment details section */}
                                <Box className="showAdditionalWrap subSegment">
                                    <Typography
                                        onClick={() => toggleSection(sectorIndex === 0 ? 'open2' : 'open3')}
                                        component="span"
                                        className='showAdditional'
                                    >
                                        {(sectorIndex === 0 ? state.open2 : state.open3) ?
                                            '▼ Hide Segment Details' : '▶ Show Segment Details'}
                                    </Typography>

                                    <Collapse in={sectorIndex === 0 ? state.open2 : state.open3}>
                                        {/* Map through segments array to render each segment */}
                                        {sector.segments && sector.segments.map((segment, segmentIndex) => (
                                            <React.Fragment key={segmentIndex}>
                                                {/* Show layover information between segments */}
                                                {segmentIndex > 0 && (
                                                    <Box className="layoverDetailsWrap">
                                                        {(() => {
                                                            const prevDestination = sector.segments[segmentIndex - 1]?.destination || '';
                                                            const layoverTime = getLayoverTimeFromStopDetails(sector.stopDetails, prevDestination);

                                                            return layoverTime ?
                                                                `${layoverTime} ⏱️ Layover in ${prevDestination} - Connect to next flight` :
                                                                `⏱️ Layover in ${prevDestination} - Connect to next flight`;
                                                        })()}
                                                    </Box>
                                                )}

                                                {/* Segment route information */}
                                                <div className="layoverFlightRoute">
                                                    {segment.origin || 'Origin'} → {segment.destination || 'Destination'}
                                                </div>

                                                {/* Segment fields */}
                                                <Box className="feeInputFieldWrap">
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Cabin Class</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.cabinClass || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'cabinClass', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>RBD</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.fareClass || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'fareClass', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Check-in Baggage</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.checkedBaggage || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'checkedBaggage', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Cabin Baggage</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.cabinBaggage || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'cabinBaggage', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Meals</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.meals || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'meals', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Seat</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.seat || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'seat', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Aircraft Type</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.aircraft || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'aircraft', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Journey Duration</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.duration || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'duration', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Departure Terminal</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.departureTerminal || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'departureTerminal', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Arrival Terminal</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.arrivalTerminal || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'arrivalTerminal', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Departure Time</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={formatDateForDisplay(segment.departureTime) || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'departureTime', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Arrival Time</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={formatDateForDisplay(segment.arrivalTime) || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'arrivalTime', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Flight Number</label>
                                                        <input
                                                            type="text"
                                                            className="inputField"
                                                            value={segment.flightNumber || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'flightNumber', e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="form-control inputFieldWrap">
                                                        <label>Airline PNR</label>
                                                        <input
                                                            type="text"
                                                            className={state.actionType == 'Confirm Booking' ? "inputField notThis" : "inputField"}
                                                            value={segment.airline_pnr || ''}
                                                            onChange={(e) => updateSegmentField(sectorIndex, segmentIndex, 'airline_pnr', e.target.value)}
                                                        />

                                                        {errors.sectors[sectorIndex]?.segments?.[segmentIndex] && (
                                                            <small className="errorMessage">
                                                                {errors.sectors[sectorIndex]?.segments?.[segmentIndex]}
                                                            </small>
                                                        )}
                                                    </div>
                                                </Box>
                                            </React.Fragment>
                                        ))}
                                    </Collapse>
                                </Box>
                            </Box>
                        ))}

                        {/* Empty state if no sectors are available */}
                        {(!state.sectorDetails || state.sectorDetails.length === 0) && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    No flight sectors available. Please retrieve booking details first.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Accordion expanded={state.open2} onChange={() => dispatch({ type: 'TOGGLE_SECTION', section: 'open2' })} className='flightMgmt'>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                >
                    <Typography component="span">Passenger Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box className="feeModalForm">
                        {state.passengerDetails && state.passengerDetails.length > 0 ? (
                            state.passengerDetails.map((passenger: PassengerDetail) => (
                                <Box key={passenger.passengerId} className="segmentCard">
                                    <div className="passengerHeader">
                                        <div className="passengerName">
                                            {passenger.title} {passenger.firstName} {passenger.lastName}
                                            {passenger.passengerId === state.passengerDetails[0]?.passengerId && <span> (Lead)</span>}
                                        </div>
                                        <div className="passengerType">
                                            {passenger.passengerType === 'ADT' ? 'Adult' :
                                                passenger.passengerType === 'CHD' ? 'Child' :
                                                    passenger.passengerType === 'INF' ? 'Infant' :
                                                        passenger.passengerType}
                                        </div>
                                    </div>
                                    <Box className="feeInputFieldWrap">
                                        <div className="form-control inputFieldWrap">
                                            <label>Title</label>
                                            <FormControl sx={{ minWidth: 315 }} className="commondropdownWrap">
                                                <Select
                                                    className="commondropdown"
                                                    id={`title-select-${passenger.passengerId}`}
                                                    value={passenger.title || ''}
                                                    onChange={(e) => dispatch({
                                                        type: 'UPDATE_PASSENGER_FIELD',
                                                        passengerId: passenger.passengerId,
                                                        field: 'title',
                                                        value: e.target.value
                                                    })}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (selected === '') {
                                                            return <span style={{ color: '#888' }}>Select Title</span>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem value="">Select Title</MenuItem>
                                                    {titles.map((title) => (
                                                        <MenuItem key={title} value={title}>{title}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                className="inputField"
                                                value={passenger.firstName || ''}
                                                onChange={(e) => dispatch({
                                                    type: 'UPDATE_PASSENGER_FIELD',
                                                    passengerId: passenger.passengerId,
                                                    field: 'firstName',
                                                    value: e.target.value
                                                })}
                                            />
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                className="inputField"
                                                value={passenger.lastName || ''}
                                                onChange={(e) => dispatch({
                                                    type: 'UPDATE_PASSENGER_FIELD',
                                                    passengerId: passenger.passengerId,
                                                    field: 'lastName',
                                                    value: e.target.value
                                                })}
                                            />
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Middle Name</label>
                                            <input
                                                type="text"
                                                className="inputField"
                                                value={passenger.middleName || ''}
                                                onChange={(e) => dispatch({
                                                    type: 'UPDATE_PASSENGER_FIELD',
                                                    passengerId: passenger.passengerId,
                                                    field: 'middleName',
                                                    value: e.target.value
                                                })}
                                            />
                                        </div>
                                        <div className="form-control inputFieldWrap">
                                            <label>Nationality</label>
                                            <input
                                                type="text"
                                                className="inputField"
                                                value={passenger.nationality || ''}
                                                onChange={(e) => dispatch({
                                                    type: 'UPDATE_PASSENGER_FIELD',
                                                    passengerId: passenger.passengerId,
                                                    field: 'nationality',
                                                    value: e.target.value
                                                })}
                                            />
                                        </div>
                                        {passenger.sectorPricing == undefined &&
                                            <div className="form-control inputFieldWrap">
                                                <label>Ticket Number</label>
                                                <input
                                                    type="text"
                                                    className={state.actionType == 'Cancellation' ? "inputFiled" : "inputField notThis"}
                                                    value={passenger.ticketNumber || ''}
                                                    onChange={(e) => dispatch({
                                                        type: 'UPDATE_PASSENGER_FIELD',
                                                        passengerId: passenger.passengerId,
                                                        field: 'ticketNumber',
                                                        value: e.target.value
                                                    })}
                                                />
                                            </div>
                                        }
                                        <div className="form-control inputFieldWrap">
                                            <label>Status</label>
                                            <FormControl sx={{ minWidth: 315 }} className="commondropdownWrap">
                                                <Select
                                                    className="commondropdown notThis"
                                                    id={`status-select-${passenger.passengerId}`}
                                                    value={passenger.status || ''}
                                                    onChange={(e) => dispatch({
                                                        type: 'UPDATE_PASSENGER_FIELD',
                                                        passengerId: passenger.passengerId,
                                                        field: 'status',
                                                        value: e.target.value
                                                    })}
                                                    displayEmpty
                                                    renderValue={(selected) => {
                                                        if (selected === '') {
                                                            return <span style={{ color: '#888' }}>Select Status</span>;
                                                        }
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem value="">Select Status</MenuItem>
                                                    {['pending', 'confirmed', 'ticketed', 'cancelled'].map((status) => (
                                                        <MenuItem key={status} value={status}>{status}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </Box>
                                    <Box className="showAdditionalWrap">
                                        <Typography
                                            onClick={() => togglePassengerDetails(passenger.passengerId)}
                                            component="span"
                                            className='showAdditional'
                                        >
                                            {expandedPassengers[passenger.passengerId] ? '▼ Hide Additional Details' : '▶ Show Additional Details'}
                                        </Typography>
                                        <Collapse in={expandedPassengers[passenger.passengerId]}>
                                            <Box className="feeInputFieldWrap">
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <div className="form-control inputFieldWrap">
                                                        <label>Date of Birth</label>
                                                        <div>
                                                            <DatePicker
                                                                value={passenger.dateOfBirth ? dayjs(passenger.dateOfBirth) : null}
                                                                onChange={(newValue) => dispatch({
                                                                    type: 'UPDATE_PASSENGER_FIELD',
                                                                    passengerId: passenger.passengerId,
                                                                    field: 'dateOfBirth',
                                                                    value: newValue ? newValue.format('YYYY-MM-DD') : null
                                                                })}
                                                                slotProps={{ textField: { fullWidth: true } }}
                                                                className='datePickerWrap'
                                                            />
                                                        </div>
                                                    </div>
                                                </LocalizationProvider>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Seat Number</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.seatNumber || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'seatNumber',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Meal Preference</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.mealPreference || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'mealPreference',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Baggage Preference</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.baggagePreference || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'baggagePreference',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Special Assistance</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.specialAssistance || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'specialAssistance',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Frequent Flyer Number</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.frequentFlyerNumber || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'frequentFlyerNumber',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Frequent Flyer Airline</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.frequentFlyerAirline || ''}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'frequentFlyerAirline',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Base Fare</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.fareAmount || '0.00'}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'fareAmount',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Tax Amount</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.taxAmount || '0.00'}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'taxAmount',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Markup</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.markupAmount || '0.00'}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'markupAmount',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Service Fee</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.serviceFeeAmount || '0.00'}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'serviceFeeAmount',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>
                                                <div className="form-control inputFieldWrap">
                                                    <label>Total Amount</label>
                                                    <input
                                                        type="text"
                                                        className="inputField"
                                                        value={passenger.totalAmount || '0.00'}
                                                        onChange={(e) => dispatch({
                                                            type: 'UPDATE_PASSENGER_FIELD',
                                                            passengerId: passenger.passengerId,
                                                            field: 'totalAmount',
                                                            value: e.target.value
                                                        })}
                                                    />
                                                </div>

                                                
                                            </Box>
                                            {/* Sector Pricing Section - Only if available */}
                                                {passenger.sectorPricing && passenger.sectorPricing.length > 0 && (
                                                    <>
                                                        <div className="sectorPricingHeader">
                                                            <Typography variant="subtitle1" component="h3">
                                                                Sector Pricing Details
                                                            </Typography>
                                                        </div>

                                                        {passenger.sectorPricing.map((sector, idx) => (
                                                            <Box key={sector.sectorId || idx}
                                                                className="sectorPricingItem"
                                                                >
                                                                <Typography variant="subtitle2" component="h4">
                                                                    Sector {idx + 1}: {sector.route || 'Route not specified'}
                                                                </Typography>
                                                                {/* <div className="form-control inputFieldWrap">
                                                                        <label>Transaction ID</label>
                                                                        <input
                                                                            type="text"
                                                                            className="inputField"
                                                                            value={sector.transactionId || ''}
                                                                            disabled
                                                                        />
                                                                    </div> */}
                                                                    <Box className="feeInputFieldWrap">
                                                                <div className="form-control inputFieldWrap">
                                                                    <label>Fare Amount</label>
                                                                    <input
                                                                        type="text"
                                                                        className="inputField"
                                                                        value={sector.fareAmount || '0.00'}
                                                                        onChange={(e) => dispatch({
                                                                            type: 'UPDATE_PASSENGER_SECTOR_PRICING',
                                                                            passengerId: passenger.passengerId,
                                                                            sectorPricingIndex: idx,
                                                                            field: 'fareAmount',
                                                                            value: e.target.value
                                                                        })}
                                                                    />
                                                                </div>
                                                                <div className="form-control inputFieldWrap">
                                                                    <label>Tax Amount</label>
                                                                    <input
                                                                        type="text"
                                                                        className="inputField"
                                                                        value={sector.taxAmount || '0.00'}
                                                                        onChange={(e) => dispatch({
                                                                            type: 'UPDATE_PASSENGER_SECTOR_PRICING',
                                                                            passengerId: passenger.passengerId,
                                                                            sectorPricingIndex: idx,
                                                                            field: 'taxAmount',
                                                                            value: e.target.value
                                                                        })}
                                                                    />
                                                                </div>
                                                                <div className="form-control inputFieldWrap">
                                                                    <label>Total Amount</label>
                                                                    <input
                                                                        type="text"
                                                                        className="inputField"
                                                                        value={sector.totalAmount || '0.00'}
                                                                        onChange={(e) => dispatch({
                                                                            type: 'UPDATE_PASSENGER_SECTOR_PRICING',
                                                                            passengerId: passenger.passengerId,
                                                                            sectorPricingIndex: idx,
                                                                            field: 'totalAmount',
                                                                            value: e.target.value
                                                                        })}
                                                                    />
                                                                </div>
                                                                <div className="form-control inputFieldWrap">
                                                                    <label>Ticket Number</label>
                                                                    <input
                                                                        type="text"
                                                                        className={state.actionType == 'Cancellation' ? "inputField" : "inputField notThis"}
                                                                        value={sector.ticketNumber || ''}
                                                                        onChange={(e) => dispatch({
                                                                            type: 'UPDATE_PASSENGER_SECTOR_PRICING',
                                                                            passengerId: passenger.passengerId,
                                                                            sectorPricingIndex: idx,
                                                                            field: 'ticketNumber',
                                                                            value: e.target.value
                                                                        })}
                                                                    />
                                                                </div>
                                                                <div className="form-control inputFieldWrap">
                                                                    <label>Status</label>
                                                                    <FormControl sx={{ minWidth: 315 }} className="commondropdownWrap">
                                                                        <Select
                                                                            className="commondropdown notThis"
                                                                            id={`sector-status-select-${passenger.passengerId}-${idx}`}
                                                                            value={sector.status || ''}
                                                                            onChange={(e) => dispatch({
                                                                                type: 'UPDATE_PASSENGER_SECTOR_PRICING',
                                                                                passengerId: passenger.passengerId,
                                                                                sectorPricingIndex: idx,
                                                                                field: 'status',
                                                                                value: e.target.value
                                                                            })}
                                                                            displayEmpty
                                                                            renderValue={(selected) => {
                                                                                if (selected === '') {
                                                                                    return <span style={{ color: '#888' }}>Select Status</span>;
                                                                                }
                                                                                return selected;
                                                                            }}
                                                                        >
                                                                            <MenuItem value="">Select Status</MenuItem>
                                                                            {['pending', 'confirmed', 'ticketed', 'cancelled'].map((status) => (
                                                                                <MenuItem key={status} value={status}>{status}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>
                                                                </div>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </>
                                                )}
                                        </Collapse>
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    No passenger details available. Please retrieve booking details first.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Status Information Section */}
            {/* <Accordion 
          expanded={state.open4} 
          onChange={() => toggleSection('open4')}
          className="accordionWrap"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Status Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="feeInputFieldWrap">
              <div className="form-control inputFieldWrap">
                <label>Status</label>
                <FormControl sx={{ minWidth: 250 }} className="commondropdownWrap">
                  <Select
                    className="commondropdown"
                    value={state.status}
                    onChange={handleSelectChange('status')}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        return <span style={{ color: '#888' }}>Select Status</span>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">
                      Select Status
                    </MenuItem>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </Box>
          </AccordionDetails>
        </Accordion> */}

            {/* Action Buttons */}
            <Box className="searchInputFilterBox" sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    className="cancelBtn"
                    variant="outlined"
                    color="secondary"
                    onClick={resetForm}
                >
                    Cancel
                </Button>
                <Button
                    className="searchBtn"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={state.isLoading}
                >
                    Submit
                </Button>

                {/* Add the confirmation dialog to your component */}
                <ConfirmationDialog />
            </Box>
        </>
    );
}
export default FlightManagement;